/**
 * Profile API Service for AmalQ
 * Comprehensive API service for user profile operations
 * Following enterprise-grade patterns from existing codebase
 */

import { apiClient, userEndpoints } from '../../../lib/api';
import { isCaptchaChallenge } from '../../../lib/captcha';
import {
  ProfileResponse,
  ProfileUpdateRequest,
  ProfileCompletionResponse,
  AvatarUploadResponse,
  OrganizationLogoUploadResponse,
  VerificationStatusResponse,
  EnhancedProfileCompletionResponse,
  SectionCompletionQuery,
  EmailVerificationStatusResponse,
  ResendVerificationEmailResponse,
} from '../types/profile.types';

// Auto-save response types
export interface AutoSaveResponse {
  success: boolean;
  data: {
    updatedFields: string[];
    autoSaveId: string;
    timestamp: string;
  };
  message: string;
  timestamp: string;
}

// Custom error class that preserves API error details
export class ProfileApiError extends Error {
  errors?: Array<{ field: string; message: string; code: string }>;
  statusCode?: number;

  constructor(
    message: string,
    errors?: Array<{ field: string; message: string; code: string }>,
    statusCode?: number
  ) {
    super(message);
    this.name = 'ProfileApiError';
    this.errors = errors;
    this.statusCode = statusCode;
  }
}

// Field validation response types
export interface FieldValidationResponse {
  success: boolean;
  data: {
    isValid: boolean;
    fieldName: string;
    fieldType: string;
    errors: string[];
    suggestions?: string[];
  };
  message: string;
  timestamp: string;
}

export interface FieldValidationRequest {
  fieldName: string;
  fieldValue: string | number | boolean | null;
  fieldType: string;
}

/**
 * Profile API Service
 * Complete implementation of all profile endpoints
 */
export const profileApi = {
  /**
   * Get user profile
   * GET /api/v1/profile
   */
  async getProfile(): Promise<ProfileResponse> {
    try {
      const response = await apiClient.get<ProfileResponse>('/profile');
      
      if (response.success && response.data) {
        // The API client wraps the server response, so response.data is the full ProfileResponse
        return response.data as ProfileResponse;
      }

      throw new Error(response.message || 'Failed to fetch profile');
    } catch (error) {
      console.error('Profile API Error in getProfile:', error);
      throw error;
    }
  },

  /**
   * Update user profile
   * PUT /api/v1/profile
   */
  async updateProfile(data: ProfileUpdateRequest): Promise<ProfileResponse> {
    try {
      const response = await apiClient.put<ProfileResponse>('/profile', data);
      
      if (response.success && response.data) {
        // The API client wraps the server response, so response.data is the full ProfileResponse
        return response.data as ProfileResponse;
      }

      throw new Error(response.message || 'Failed to update profile');
    } catch (error) {
      console.error('Profile API Error in updateProfile:', error);
      throw error;
    }
  },

  /**
   * Get profile completion percentage
   * GET /api/v1/profile/completion
   */
  async getProfileCompletion(): Promise<ProfileCompletionResponse> {
    try {
      const response = await apiClient.get<ProfileCompletionResponse>('/profile/completion');
      
      if (response.success && response.data) {
        // The API client wraps the server response, so response.data is the full ProfileCompletionResponse
        return response.data as ProfileCompletionResponse;
      }

      throw new Error(response.message || 'Failed to fetch profile completion');
    } catch (error) {
      console.error('Profile API Error in getProfileCompletion:', error);
      throw error;
    }
  },

  /**
   * Upload user avatar
   * POST /api/v1/profile/avatar
   */
  async uploadAvatar(file: File): Promise<AvatarUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await apiClient.post<AvatarUploadResponse['data']>('/profile/avatar', formData);
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Avatar uploaded successfully',
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to upload avatar');
    } catch (error) {
      console.error('Profile API Error in uploadAvatar:', error);
      throw error;
    }
  },

  /**
   * Delete user avatar
   * DELETE /api/v1/profile/avatar
   */
  async deleteAvatar(): Promise<ProfileResponse> {
    try {
      const response = await apiClient.delete<ProfileResponse['data']>('/profile/avatar');
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Avatar deleted successfully',
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to delete avatar');
    } catch (error) {
      console.error('Profile API Error in deleteAvatar:', error);
      throw error;
    }
  },

  /**
   * Upload organization logo
   * POST /api/v1/profile/organization-logo
   */
  async uploadOrganizationLogo(file: File): Promise<OrganizationLogoUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('organizationLogo', file);
      
      const response = await apiClient.post<OrganizationLogoUploadResponse['data']>('/profile/organization-logo', formData);
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Organization logo uploaded successfully',
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to upload organization logo');
    } catch (error) {
      console.error('Profile API Error in uploadOrganizationLogo:', error);
      throw error;
    }
  },

  /**
   * Get verification status
   * GET /api/v1/profile/verification-status
   *
   * Note: apiClient.get wraps the backend response, so response.data contains the full
   * VerificationStatusResponse from the backend. We return it directly (like getProfile does)
   * to avoid double-wrapping.
   */
  async getVerificationStatus(): Promise<VerificationStatusResponse> {
    try {
      const response = await apiClient.get<VerificationStatusResponse>('/profile/verification-status');

      if (response.success && response.data) {
        // The API client wraps the server response, so response.data is the full VerificationStatusResponse
        return response.data as VerificationStatusResponse;
      }

      throw new Error(response.message || 'Failed to fetch verification status');
    } catch (error) {
      console.error('Profile API Error in getVerificationStatus:', error);
      throw error;
    }
  },

  /**
   * Auto-save profile data
   * PATCH /api/v1/profile/auto-save
   */
  async autoSaveProfile(data: Partial<ProfileUpdateRequest>): Promise<AutoSaveResponse> {
    try {
      const response = await apiClient.patch<AutoSaveResponse['data']>('/profile/auto-save', data);

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Profile auto-saved successfully',
          timestamp: new Date().toISOString(),
        };
      }

      // Check for validation errors in the response
      // The apiClient returns { success: false, data: { errors: [...], message: "..." } }
      const errorData = response.data as { errors?: Array<{ field: string; message: string; code: string }> };
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        throw new ProfileApiError(
          response.message || 'Validation failed',
          errorData.errors
        );
      }

      throw new ProfileApiError(response.message || 'Failed to auto-save profile');
    } catch (error) {
      // Re-throw ProfileApiError as-is to preserve error details
      if (error instanceof ProfileApiError) {
        throw error;
      }
      console.error('Profile API Error in autoSaveProfile:', error);
      throw error;
    }
  },

  /**
   * Validate individual field
   * POST /api/v1/profile/validate-field
   */
  async validateField(data: FieldValidationRequest): Promise<FieldValidationResponse> {
    try {
      const response = await apiClient.post<FieldValidationResponse['data']>('/profile/validate-field', data);
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Field validation completed',
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to validate field');
    } catch (error) {
      console.error('Profile API Error in validateField:', error);
      throw error;
    }
  },

  /**
   * Get enhanced profile completion with section-specific data
   * GET /api/v1/profile/completion/enhanced
   */
  async getEnhancedProfileCompletion(query?: SectionCompletionQuery): Promise<EnhancedProfileCompletionResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (query?.section && query.section !== 'all') {
        queryParams.append('section', query.section);
      }
      if (query?.includeDetails) {
        queryParams.append('includeDetails', 'true');
      }

      const url = `/profile/completion/enhanced${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get<EnhancedProfileCompletionResponse>(url);

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch enhanced profile completion');
    } catch (error) {
      console.error('Profile API Error in getEnhancedProfileCompletion:', error);
      throw error;
    }
  },

  /**
   * Get section-specific completion data
   * GET /api/v1/profile/completion/enhanced?section={section}
   */
  async getSectionCompletion(section: 'personal' | 'organizer' | 'organization' | 'preferences'): Promise<EnhancedProfileCompletionResponse> {
    try {
      const response = await apiClient.get<EnhancedProfileCompletionResponse>(`/profile/completion/enhanced?section=${section}`);

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error(response.message || 'Failed to fetch section completion');
    } catch (error) {
      console.error('Profile API Error in getSectionCompletion:', error);
      throw error;
    }
  },

  /**
   * Get email verification status
   * GET /api/v1/auth/email-verification-status
   *
   * Note: apiClient.get wraps the backend response, so response.data contains the full
   * EmailVerificationStatusResponse from the backend. We return it directly (like getProfile does)
   * to avoid double-wrapping.
   */
  async getEmailVerificationStatus(): Promise<EmailVerificationStatusResponse> {
    try {
      const response = await apiClient.get<EmailVerificationStatusResponse>(
        userEndpoints.auth.emailVerificationStatus()
      );

      if (response.success && response.data) {
        // The API client wraps the server response, so response.data is the full EmailVerificationStatusResponse
        return response.data as EmailVerificationStatusResponse;
      }

      throw new Error(response.message || 'Failed to fetch email verification status');
    } catch (error) {
      console.error('Profile API Error in getEmailVerificationStatus:', error);
      throw error;
    }
  },

  /**
   * Resend verification email
   * POST /api/v1/auth/resend-verification-email
   * Supports optional hCaptcha token for risk-based CAPTCHA
   * Returns CAPTCHA challenge if risk assessment requires it
   */
  async resendVerificationEmail(hcaptchaToken?: string): Promise<ResendVerificationEmailResponse> {
    try {

      const response = await apiClient.post<ResendVerificationEmailResponse>(
        userEndpoints.auth.resendVerificationEmail(),
        hcaptchaToken ? { hcaptchaToken } : undefined
      );

      // Check if API client returned a CAPTCHA challenge or structured error response
      if (response.success === false && response.data) {
        return response.data as ResendVerificationEmailResponse;
      }

      const responseData = response.data;

      if (!responseData) {
        throw new Error('No response data received');
      }

      // Check if response is a CAPTCHA challenge (risk-based security)
      if (isCaptchaChallenge(responseData)) {
        return responseData;
      }

      // Success response
      if ('success' in responseData && responseData.success) {
        return responseData;
      }

      // Wrap the data in proper response format
      return {
        success: true,
        data: responseData as ResendVerificationEmailResponse extends { data?: infer D } ? D : never,
        message: response.message || 'Verification email sent successfully',
        timestamp: new Date().toISOString(),
      } as ResendVerificationEmailResponse;
    } catch (error) {
      console.error('Profile API Error in resendVerificationEmail:', error);
      throw error;
    }
  },

  /**
   * Verify email using 6-digit code
   * POST /api/v1/auth/verify-email
   *
   * Note: apiClient.post wraps the backend response, so response.data contains the full
   * server response body. We return it directly (like getEmailVerificationStatus does)
   * to avoid double-wrapping.
   */
  async verifyEmailCode(code: string): Promise<EmailVerificationStatusResponse> {
    try {
      const response = await apiClient.post<EmailVerificationStatusResponse>(
        '/auth/verify-email',
        { code }
      );

      if (response.success && response.data) {
        // The API client wraps the server response, so response.data is the full server response body
        // Return it directly to avoid double-wrapping
        return response.data as EmailVerificationStatusResponse;
      }

      throw new Error(response.message || 'Failed to verify email');
    } catch (error) {
      console.error('Profile API Error in verifyEmailCode:', error);
      throw error;
    }
  },
};
